import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { getLanguage } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { formatCompactAge } from '../../components/translations/coverageStatus';
import { StatusLedDot } from '../../components/translations/TranslationStatusChip';
import { testIds } from './testIds';

export type QueueGroupRow = {
  sourceKind: 'CONVERSATION' | 'LEARNER_GUIDE' | 'VIDEO_SUBTITLE';
  sourceId: string;
  language: string;
  state: 'queued' | 'generating' | 'failed';
  stalled: boolean;
  resourceTitle: string | null;
  unitTitle: string | null;
  ageSeconds: number;
  startedAt: Date | string | null;
  updatedAt: Date | string;
};

export type QueueGroup = {
  courseId: string | null;
  courseTitle: string | null;
  queued: number;
  generating: number;
  failed: number;
  oldestAgeSeconds: number;
  rows: QueueGroupRow[];
};

const ROWS_SHOWN_COLLAPSED = 20;

// One column template shared by the header row and every data row, in both the
// Active and Failed sections, so Resource/Language/State/Started/Age line up
// down the whole page instead of each course group inventing its own widths
// (the per-group `<Table>` this replaces was the source of that).
//
// Resource is the only flexible track: it grows to however much room is
// going spare and only truncates a title that's genuinely too long to read,
// rather than an arbitrary cap. The metadata columns (Language/State/Started/
// Age/action) are fixed-width and stay glued together as a block regardless
// of how wide Resource grows — the same layout a file list or inbox uses for
// its name column, and it needs no dedicated spacer track.
const QUEUE_COLS = `minmax(0, 1fr) ${pxToRem(130)} ${pxToRem(120)} ${pxToRem(110)} ${pxToRem(64)} ${pxToRem(40)}`;

function kindLabel(kind: QueueGroupRow['sourceKind']): string {
  switch (kind) {
    case 'CONVERSATION':
      return t`Conversation`;
    case 'LEARNER_GUIDE':
      return t`Learner guide`;
    case 'VIDEO_SUBTITLE':
      return t`Video subtitles`;
  }
}

function languageLabel(code: string): string {
  return getLanguage(code)?.englishName ?? code;
}

// "9:41am" for today, "13 Jul, 4:10pm" otherwise, so a stuck run from days ago
// doesn't read as an ambiguous bare time.
export function formatAbsoluteTime(value: Date | string): string {
  const date = djs(value);
  return date.isSame(djs(), 'day') ? date.format('h:mma') : date.format('D MMM, h:mma');
}

// Splits what the shared chip deliberately merges: "Queued" (hollow dot) vs
// "Translating" (filled breathing dot), on the same StatusLedDot vocabulary.
function QueueStateChip({ state }: { state: QueueGroupRow['state'] }) {
  if (state === 'failed') {
    return (
      <Badge
        variant="pill"
        fontSize="xs"
        fontWeight="semibold"
        bg="red.50"
        color="red.700"
        boxShadow="inset 0 0 0 1px var(--chakra-colors-red-200)"
      >
        <HStack spacing="1.5" as="span">
          <StatusLedDot status="failed" />
          <Trans>Failed</Trans>
        </HStack>
      </Badge>
    );
  }
  return (
    <Badge
      variant="pill"
      fontSize="xs"
      fontWeight="semibold"
      bg="blue.50"
      color="blue.700"
      boxShadow="inset 0 0 0 1px var(--chakra-colors-blue-200)"
    >
      <HStack spacing="1.5" as="span">
        {state === 'queued' ? (
          <Box
            boxSize="2"
            borderRadius="full"
            flexShrink={0}
            role="presentation"
            aria-hidden
            boxShadow="inset 0 0 0 1.5px var(--chakra-colors-blue-500)"
          />
        ) : (
          <StatusLedDot status="generating" />
        )}
        {state === 'queued' ? <Trans>Queued</Trans> : <Trans>Translating</Trans>}
      </HStack>
    </Badge>
  );
}

function groupSummary(group: QueueGroup): string {
  const parts: string[] = [];
  if (group.queued > 0) {
    parts.push(t`${group.queued} queued`);
  }
  if (group.generating > 0) {
    parts.push(t`${group.generating} translating`);
  }
  if (group.failed > 0) {
    parts.push(t`${group.failed} failed`);
  }
  return parts.join(' · ');
}

function ColumnHeader({ view }: { view: 'pending' | 'failed' }) {
  return (
    <Grid
      templateColumns={QUEUE_COLS}
      columnGap={4}
      px={3}
      py={2}
      bg="bg.subtle"
      borderBottomWidth="1px"
      borderColor="border.default"
    >
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="fg.muted">
        <Trans>Resource</Trans>
      </Text>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="fg.muted">
        <Trans>Language</Trans>
      </Text>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="fg.muted">
        <Trans>State</Trans>
      </Text>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="fg.muted">
        {view === 'pending' ? <Trans>Started</Trans> : <Trans>Failed at</Trans>}
      </Text>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        textTransform="uppercase"
        color="fg.muted"
        justifySelf="end"
      >
        <Trans>Age</Trans>
      </Text>
      <Box aria-hidden />
    </Grid>
  );
}

function QueueRow({
  row,
  view,
  busy,
  onCancelRow,
  onRetryRow,
}: {
  row: QueueGroupRow;
  view: 'pending' | 'failed';
  busy: boolean;
  onCancelRow: () => void;
  onRetryRow: () => void;
}) {
  // Started shows the claim time for a run in progress, "—" for a row that
  // hasn't started yet. Failed shows the instant it was marked failed
  // (updatedAt — there is no dedicated failure timestamp).
  const startedLabel =
    view === 'failed'
      ? formatAbsoluteTime(row.updatedAt)
      : row.startedAt === null
        ? '—'
        : formatAbsoluteTime(row.startedAt);

  return (
    <Grid
      role="group"
      templateColumns={QUEUE_COLS}
      columnGap={4}
      alignItems="center"
      px={3}
      py={2}
      borderTopWidth="1px"
      borderColor="border.default"
      _hover={{ bg: 'bg.subtle' }}
    >
      <HStack spacing={1} minW={0}>
        <Text fontSize="sm" noOfLines={1}>
          {row.unitTitle !== null && row.resourceTitle !== null
            ? `${row.unitTitle} · ${row.resourceTitle}`
            : (row.resourceTitle ?? row.sourceId)}
        </Text>
        <Text fontSize="xs" color="fg.muted" flexShrink={0}>
          · {kindLabel(row.sourceKind)}
        </Text>
      </HStack>
      <Text fontSize="sm" color="fg.muted" noOfLines={1}>
        {languageLabel(row.language)}
      </Text>
      <Box>
        <QueueStateChip state={row.state} />
      </Box>
      <Text fontSize="sm" color="fg.muted" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {startedLabel}
      </Text>
      {row.stalled ? (
        <Tooltip
          label={t`Started ${formatCompactAge(row.ageSeconds)} ago — over the 15 minute expected duration. Likely a stalled worker.`}
        >
          <HStack spacing={1} justifySelf="end" color="orange.600" cursor="help">
            <Text fontSize="sm" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCompactAge(row.ageSeconds)}
            </Text>
          </HStack>
        </Tooltip>
      ) : (
        <Text
          fontSize="sm"
          color="fg.muted"
          justifySelf="end"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCompactAge(row.ageSeconds)}
        </Text>
      )}
      {view === 'pending' ? (
        <Tooltip label={t`Cancel translation`} openDelay={200}>
          <IconButton
            aria-label={t`Cancel translation`}
            icon={<X size={iconSize.sm} />}
            size="xs"
            variant="ghost"
            color="fg.muted"
            isDisabled={busy}
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _focusVisible={{ opacity: 1 }}
            transition="opacity 0.15s"
            justifySelf="end"
            data-testid={testIds.rowCancelButton}
            onClick={onCancelRow}
          />
        </Tooltip>
      ) : (
        <Tooltip label={t`Retry translation`} openDelay={200}>
          <IconButton
            aria-label={t`Retry translation`}
            icon={<RefreshCw size={iconSize.sm} />}
            size="xs"
            variant="ghost"
            color="fg.muted"
            isDisabled={busy || row.sourceKind === 'VIDEO_SUBTITLE'}
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _focusVisible={{ opacity: 1 }}
            transition="opacity 0.15s"
            justifySelf="end"
            onClick={onRetryRow}
          />
        </Tooltip>
      )}
    </Grid>
  );
}

function CourseGroup({
  group,
  isLast,
  view,
  busy,
  soleGroup,
  onCancelRow,
  onCancelCourse,
  onRetryRow,
}: {
  group: QueueGroup;
  isLast: boolean;
  view: 'pending' | 'failed';
  busy: boolean;
  soleGroup: boolean;
  onCancelRow: (row: QueueGroupRow, scopeLabel: string) => void;
  onCancelCourse: (group: QueueGroup) => void;
  onRetryRow: (row: QueueGroupRow) => void;
}) {
  // Problems open, healthy groups collapse to headers.
  const needsALook = group.failed > 0 || group.rows.some((row) => row.stalled);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: needsALook || soleGroup });
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? group.rows : group.rows.slice(0, ROWS_SHOWN_COLLAPSED);

  return (
    <Box
      borderBottomWidth={isLast ? undefined : '1px'}
      borderColor="border.default"
      data-testid={testIds.courseGroup}
    >
      <Flex align="center" gap={3} py={2} px={3} bg="bg.subtle">
        <Flex
          as="button"
          type="button"
          onClick={onToggle}
          align="center"
          gap={2}
          minW={0}
          textAlign="start"
        >
          <Icon
            as={isOpen ? ChevronDown : ChevronRight}
            boxSize={iconSize.sm}
            color="fg.muted"
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="semibold" noOfLines={1} minW={0}>
            {group.courseTitle ?? <Trans>Unlinked resources</Trans>}
          </Text>
          <Text fontSize="xs" color="fg.muted" flexShrink={0}>
            {groupSummary(group)}
          </Text>
        </Flex>
        {view === 'pending' && group.courseId !== null ? (
          <Button
            size="xs"
            variant="outline"
            isDisabled={busy}
            flexShrink={0}
            onClick={() => onCancelCourse(group)}
          >
            <Trans>Cancel All ({group.queued + group.generating})</Trans>
          </Button>
        ) : null}
        <Box flex="1" />
      </Flex>
      {isOpen ? (
        <Box>
          {rows.map((row) => (
            <QueueRow
              key={`${row.sourceKind}:${row.sourceId}:${row.language}`}
              row={row}
              view={view}
              busy={busy}
              onCancelRow={() =>
                onCancelRow(
                  row,
                  [group.courseTitle, row.resourceTitle, languageLabel(row.language)]
                    .filter(Boolean)
                    .join(' · '),
                )
              }
              onRetryRow={() => onRetryRow(row)}
            />
          ))}
          {group.rows.length > ROWS_SHOWN_COLLAPSED && !showAll ? (
            <Button size="xs" variant="ghost" w="full" mt={1} onClick={() => setShowAll(true)}>
              <Trans>Show all {group.rows.length} rows</Trans>
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export function QueueGroups({
  groups,
  view,
  onCancelCells,
  onCancelCourse,
  onRetryRow,
  busy,
}: {
  groups: QueueGroup[];
  view: 'pending' | 'failed';
  onCancelCells: (cells: QueueGroupRow[], scopeLabel: string) => Promise<void>;
  onCancelCourse: (courseId: string, courseTitle: string) => Promise<void>;
  onRetryRow: (row: QueueGroupRow) => Promise<void>;
  busy: boolean;
}) {
  const [rowToCancel, setRowToCancel] = useState<{ row: QueueGroupRow; scopeLabel: string } | null>(
    null,
  );
  const [courseToCancel, setCourseToCancel] = useState<{
    id: string;
    title: string;
    pending: number;
  } | null>(null);

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden">
      <ColumnHeader view={view} />
      {groups.map((group, index) => (
        <CourseGroup
          key={group.courseId ?? 'unlinked'}
          group={group}
          isLast={index === groups.length - 1}
          view={view}
          busy={busy}
          soleGroup={groups.length === 1}
          onCancelRow={(row, scopeLabel) => setRowToCancel({ row, scopeLabel })}
          onCancelCourse={(courseGroup) =>
            setCourseToCancel({
              id: courseGroup.courseId ?? '',
              title: courseGroup.courseTitle ?? '',
              pending: courseGroup.queued + courseGroup.generating,
            })
          }
          onRetryRow={(row) => void onRetryRow(row)}
        />
      ))}

      {rowToCancel !== null ? (
        <ConfirmationDialog
          isOpen
          onClose={() => setRowToCancel(null)}
          title={<Trans>Cancel this translation?</Trans>}
          body={
            <Text>
              <Trans>
                {rowToCancel.scopeLabel} will be cancelled. If it's already generating, that result
                is discarded. You can request it again any time.
              </Trans>
            </Text>
          }
          confirmLabel={<Trans>Cancel translation</Trans>}
          cancelLabel={<Trans>Go back</Trans>}
          variant="destructive"
          isLoading={busy}
          onConfirm={() => {
            void onCancelCells([rowToCancel.row], rowToCancel.scopeLabel).then(() =>
              setRowToCancel(null),
            );
          }}
        />
      ) : null}

      {courseToCancel !== null ? (
        <ConfirmationDialog
          isOpen
          onClose={() => setCourseToCancel(null)}
          title={<Trans>Cancel all pending translations for this course?</Trans>}
          body={
            <Text>
              <Trans>
                {courseToCancel.pending} translations for {courseToCancel.title} will be cancelled.
                Any already generating will finish, but their results are discarded. Completed
                translations are unaffected.
              </Trans>
            </Text>
          }
          confirmLabel={<Trans>Cancel {courseToCancel.pending} translations</Trans>}
          cancelLabel={<Trans>Go back</Trans>}
          variant="destructive"
          isLoading={busy}
          onConfirm={() => {
            void onCancelCourse(courseToCancel.id, courseToCancel.title).then(() =>
              setCourseToCancel(null),
            );
          }}
        />
      ) : null}
    </Box>
  );
}
